import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import PageLayout from '../components/PageLayout';
import PageMain from '../components/ui/PageMain';
import PageBody from '../components/ui/PageBody';
import IconButton from '../components/ui/IconButton';

import { restoreArticleVersion, getArticleHistory } from '../api';

function ArticleHistory() {
    const { t } = useTranslation();
    const { id } = useParams();
    const location = useLocation();
    const canRestore = location.state?.from === 'archive';
    const navigate = useNavigate();

    const [article, setArticle] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;

        async function loadHistory() {
            try {
                setLoading(true);
                setError('');

                const versions = await getArticleHistory(id);

                if (!Array.isArray(versions)) {
                    throw new Error('Invalid history response');
                }

                const currentArticle = versions.find(
                    (item) => !item.is_archived
                );

                const archivedVersions = versions
                    .filter((item) => item.is_archived)
                    .sort((a, b) => {
                        const dateA = new Date(
                            a.modified_date || a.creation_date || 0
                        ).getTime();

                        const dateB = new Date(
                            b.modified_date || b.creation_date || 0
                        ).getTime();

                        return (
                            dateB - dateA ||
                            Number(b.article_id) - Number(a.article_id)
                        );
                    });

                const historyItems = [
                    ...(currentArticle
                        ? [{
                            ...currentArticle,
                            author_name:
                                currentArticle.author_name ??
                                currentArticle.user_name ??
                                null,
                            isCurrent: true,
                        }]
                        : []),

                    ...archivedVersions.map((item) => ({
                        ...item,
                        author_name:
                            item.author_name ??
                            item.user_name ??
                            null,
                        isCurrent: false,
                    })),
                ];

                if (!cancelled) {
                    setArticle(currentArticle || versions[0] || null);
                    setHistory(historyItems);
                }
            } catch (err) {
                console.error('Error loading article history:', err);

                if (!cancelled) {
                    setError(
                        'Die Artikelhistorie konnte nicht geladen werden.'
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        loadHistory();

        return () => {
            cancelled = true;
        };


    }, [id]);

    function formatDateTime(value) {
        if (value == null || value === '') {
            return '—';
        }

        const numeric = Number(value);

        const date = new Date(
            Number.isFinite(numeric)
                ? numeric < 1e12
                    ? numeric * 1000
                    : numeric
                : value
        );

        if (Number.isNaN(date.getTime())) {
            return '—';
        }

        return new Intl.DateTimeFormat('de-DE', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        }).format(date);

    }

    async function handleRestore(event, entry) {
        event.stopPropagation();


        const confirmed = window.confirm(
            t('articlesPage.confirmRestore')
        );

        if (!confirmed) return;

        try {
            await restoreArticleVersion(entry.article_id);
            navigate('/articles');
        } catch (err) {
            console.error(
                'Failed to restore article version:',
                err
            );
        }

    }

    function openVersion(entry) {
        if (!entry.isCurrent) {
            navigate(`/archive/article/${entry.article_id}`, {
                state: {
                    from:
                        location.state?.from === 'archive'
                            ? 'archive'
                            : location.state?.from === 'archived-articles'
                                ? 'archived-articles'
                                : 'deletedArchive',
                },
            });
            return;
        }

        if (entry.is_news) {
            navigate(`/news/${entry.article_id}`);
        } else {
            navigate(`/article/${entry.article_id}`);
        }
    }

    return (
        <PageLayout showSearch showAiSearch>
            <PageMain className="article-page">
                <div className="page-header">
                    <div
                        className="page-header__row sticky is-condensed"
                        style={{ '--page-title-shrink': 1 }}
                    >
                        <div>
                            {location.state?.from === 'archived-articles' && (
                                <div className="page-eyebrow">
                                    <Link to="/admin/werkzeuge#admin-tools-articles-title">
                                        <i className="fa-solid fa-arrow-left" aria-hidden="true" />
                                        {t('archivedArticles.backToArchive', 'Back to archived articles')}
                                    </Link>
                                </div>
                            )}
                            <h1>
                                {t('common.archive')}
                                {article?.title ? ` / ${article.title}` : ''}
                            </h1>
                        </div>
                    </div>
                </div>

                {loading && <p>Historie wird geladen …</p>}

                {error && (
                    <div
                        className="login-message login-message-error"
                        role="alert"
                    >
                        {error}
                    </div>
                )}

                {!loading && !error && history.length === 0 && (
                    <p>Keine Änderungshistorie vorhanden.</p>
                )}

                {!loading && !error && history.length > 0 && (
                    <PageBody className="articles-list-archive">
                        {history.map((entry) => (
                            <article
                                key={entry.article_id}
                                className={`article-list-item${entry.isCurrent
                                    ? ' article-list-item--current'
                                    : ''
                                    }`}
                                onClick={() => openVersion(entry)}
                                role="link"
                                tabIndex={0}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        openVersion(entry);
                                    }
                                }}
                            >
                                <div className="article-list-content">
                                    <ul className="page-header__meta history-meta">
                                        <li>
                                            <i
                                                className="fa-regular fa-calendar"
                                                aria-hidden="true"
                                            />
                                            {formatDateTime(entry.modified_date)}
                                        </li>

                                        <li>
                                            <i
                                                className="fa-regular fa-user"
                                                aria-hidden="true"
                                            />
                                            Von {entry.author_name || '—'}
                                        </li>

                                        <li>
                                            <i
                                                className="fa-regular fa-file-lines"
                                                aria-hidden="true"
                                            />
                                            <span
                                                className="history-article-name"
                                                title={entry.title || ''}
                                            >
                                                {entry.title || '—'}
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                                <div className="article-list-actions">
                                    {entry.isCurrent && (
                                        <a
                                            className="tag tag-small"
                                            href={
                                                entry.is_news
                                                    ? `/news/${entry.article_id}`
                                                    : `/article/${entry.article_id}`
                                            }
                                            title={t('articlesPage.currentVersion')}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                openVersion(entry);
                                            }}
                                        >
                                            <span className="tag-text">
                                                {t('articlesPage.currentVersion')}
                                            </span>
                                        </a>
                                    )}

                                    {!entry.isCurrent && (
                                        <IconButton
                                            className={`article-visibility-button${!canRestore ? ' is-disabled' : ''}`}
                                            icon="fa-solid fa-box-open"
                                            label={t('articlesPage.restore')}
                                            onClick={(event) => {
                                                if (!canRestore) return;
                                                handleRestore(event, entry);
                                            }}
                                            disabled={!canRestore}
                                        />
                                    )}
                                </div>
                            </article>
                        ))}
                    </PageBody>
                )}
            </PageMain>
        </PageLayout>
    );
}
export default ArticleHistory;
